import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, LogOut, Lock, Truck, CheckCircle2, AlertCircle, MapPin, Phone, Filter, Clock, Calendar, Box, X, ArrowLeft, User, History, UserCog, Search, Eye, Image as ImageIcon, Tag, Shield, ShieldCheck, Ruler, Weight, Printer, Navigation } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import BaggageTagPrint, { triggerPrint, printBaggageTagInPopup } from '../components/BaggageTagPrint';
import { formatPickupSlot } from '../lib/pickupSlots';
import { formatPickupAddress, formatDeliveryAddress } from '../lib/addressFormat';
import { ViewMapButton } from '../components/ViewMapButton';
import { CallCustomerButton } from '../components/CallCustomerButton';
import { InstallAppButton } from '../components/InstallAppButton';
import { DeliveryOtpModal } from '../components/DeliveryOtpModal';
import { usePhotoLightbox } from '../components/PhotoLightbox';
import EditAuthorisationModal from '../components/EditAuthorisationModal';
import PartnerFleetMap from '../components/PartnerFleetMap';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  NEW_BOOKING: { color: '#A5B4FC', label: 'NEW BOOKING', icon: Calendar },
  BOOKED: { color: '#52525B', label: 'BOOKED', icon: Calendar },
  PAID: { color: '#00C853', label: 'PAID', icon: CheckCircle2 },
  SCHEDULED: { color: '#FFB800', label: 'SCHEDULED', icon: Calendar },
  DELAYED_PICKUP: { color: '#DC2626', label: 'DELAYED PICKUP', icon: AlertCircle },
  PICKED_UP: { color: '#FFB800', label: 'PICKED UP', icon: Box },
  IN_TRANSIT: { color: '#FF4500', label: 'IN TRANSIT', icon: Truck },
  ARRIVED_DESTINATION: { color: '#00A651', label: 'ARRIVED AT DESTINATION', icon: MapPin },
  OUT_FOR_DELIVERY: { color: '#FF4500', label: 'OUT FOR DELIVERY', icon: Truck },
  DELAYED_DELIVERY: { color: '#DC2626', label: 'DELAYED DELIVERY', icon: AlertCircle },
  DELIVERED: { color: '#00C853', label: 'DELIVERED', icon: CheckCircle2 },
  DELAYED: { color: '#DC2626', label: 'DELAYED', icon: AlertCircle },
  REROUTED: { color: '#F59E0B', label: 'REROUTED', icon: AlertCircle },
  CANCELLED_BY_CUSTOMER: { color: '#9CA3AF', label: 'CANCELLED BY CUSTOMER', icon: AlertCircle }
};

const STATUS_OPTIONS = ['ALL', 'NEW_BOOKING', 'SCHEDULED', 'DELAYED_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'ARRIVED_DESTINATION', 'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY', 'DELIVERED', 'REROUTED', 'CANCELLED_BY_CUSTOMER'];

// Partner allowed transitions per current status.
// DELIVERED is NEVER offered here — partner must use the OTP modal to mark
// a booking DELIVERED (customer OTP verification is mandatory).
const STATUS_TRANSITIONS_PARTNER = {
  NEW_BOOKING:        ['SCHEDULED', 'REROUTED', 'CANCELLED_BY_CUSTOMER'],
  BOOKED:             ['NEW_BOOKING', 'SCHEDULED', 'REROUTED', 'CANCELLED_BY_CUSTOMER'],
  PAID:               ['NEW_BOOKING', 'SCHEDULED', 'REROUTED', 'CANCELLED_BY_CUSTOMER'],
  SCHEDULED:          ['PICKED_UP', 'DELAYED_PICKUP', 'REROUTED', 'CANCELLED_BY_CUSTOMER'],
  DELAYED_PICKUP:     ['PICKED_UP', 'REROUTED', 'CANCELLED_BY_CUSTOMER'],
  PICKED_UP:          ['IN_TRANSIT', 'ARRIVED_DESTINATION', 'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY'],
  IN_TRANSIT:         ['ARRIVED_DESTINATION', 'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY'],
  ARRIVED_DESTINATION: ['OUT_FOR_DELIVERY', 'DELAYED_DELIVERY'],
  OUT_FOR_DELIVERY:   ['DELAYED_DELIVERY'],
  DELAYED_DELIVERY:   ['OUT_FOR_DELIVERY'],
  DELAYED:            ['PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'],
  DELIVERED:          [],
  CANCELLED_BY_CUSTOMER: [],
  REROUTED:           []
};

const OTP_ELIGIBLE_STATES = new Set(['OUT_FOR_DELIVERY', 'DELAYED_DELIVERY']);

const Partner = () => {
  const navigate = useNavigate();
  const { open: openPhoto, gallery: photoGallery } = usePhotoLightbox();
  // Initialise auth state synchronously from localStorage so the back button
  // (or a fresh tab) doesn't flash the login form when a session already exists.
  const _savedPartner = (() => {
    try { return JSON.parse(localStorage.getItem('partner_session') || 'null'); }
    catch { return null; }
  })();
  const [isAuth, setIsAuth] = useState(Boolean(_savedPartner));
  const [partner, setPartner] = useState(_savedPartner);
  
  // Login form
  const [partnerCode, setPartnerCode] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Dashboard
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Status update modal
  const [editBooking, setEditBooking] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusLocation, setStatusLocation] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  
  // Verify-luggage-details modal
  const [viewingBooking, setViewingBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);  // OTP-authorised manifest edit target

  // Delivery OTP modal
  const [otpBooking, setOtpBooking] = useState(null);
  
  // Printable tag
  const [printingBooking, setPrintingBooking] = useState(null);
  
  const handlePrint = async (booking) => {
    let customer = null;
    if (booking.user_id) {
      try {
        const r = await axios.get(`${API}/users/${booking.user_id}`);
        customer = r.data;
      } catch (e) { console.warn('[partner] fetch customer for print failed:', e); }
    }
    setPrintingBooking({ booking, customer });
    printBaggageTagInPopup(booking, customer);
  };
  
  // Tabs
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' | 'fleet' | 'profile' | 'audit'
  
  // Profile
  const [profileName, setProfileName] = useState('');
  const [profileContact, setProfileContact] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  
  // Audit log
  const [auditLog, setAuditLog] = useState([]);
  
  // Agents (for assignment)
  const [availableAgents, setAvailableAgents] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('partner_session');
    if (saved) {
      const data = JSON.parse(saved);
      setPartner(data);
      setIsAuth(true);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!partner) return;
    setLoading(true);
    try {
      const [statsRes, bookingsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/partner/${partner.partner_code}/stats`),
        axios.get(`${API}/partner/${partner.partner_code}/bookings${statusFilter !== 'ALL' ? `?status=${statusFilter}` : ''}`),
        axios.get(`${API}/partner/${partner.partner_code}/agents`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings);
      setAvailableAgents(agentsRes.data.agents || []);
    } catch (err) {
      console.error('Failed to fetch partner data:', err);
    } finally {
      setLoading(false);
    }
  }, [partner, statusFilter]);

  const handleAssignAgent = async (bookingId, agentCode) => {
    try {
      await axios.post(`${API}/partner/${partner.partner_code}/bookings/${bookingId}/assign-agent`, {
        agent_code: agentCode
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to assign agent');
    }
  };

  useEffect(() => {
    if (isAuth) fetchData();
  }, [isAuth, fetchData]);

  // Escape-key dismiss for the "Verify Luggage" details modal
  useEffect(() => {
    if (!viewingBooking) return;
    const onKey = (e) => { if (e.key === 'Escape') setViewingBooking(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewingBooking]);

  useEffect(() => {
    if (partner) {
      setProfileName(partner.name || '');
      setProfileContact(partner.contact || '');
    }
  }, [partner]);

  const fetchAuditLog = useCallback(async () => {
    if (!partner) return;
    try {
      const res = await axios.get(`${API}/partner/${partner.partner_code}/audit-log`);
      setAuditLog(res.data.audit_log || []);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    }
  }, [partner]);

  useEffect(() => {
    if (isAuth && activeTab === 'audit') fetchAuditLog();
  }, [isAuth, activeTab, fetchAuditLog]);

  const handleUpdateProfile = async () => {
    setProfileMsg('');
    try {
      const res = await axios.put(`${API}/partner/${partner.partner_code}/profile`, {
        name: profileName,
        contact: profileContact
      });
      const updated = { ...partner, name: res.data.name, contact: res.data.contact };
      localStorage.setItem('partner_session', JSON.stringify(updated));
      setPartner(updated);
      setProfileMsg('Profile updated successfully');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      setProfileMsg(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleChangePassword = async () => {
    setPwMsg('');
    if (newPw.length < 4) {
      setPwMsg('New password must be at least 4 characters');
      return;
    }
    try {
      await axios.post(`${API}/partner/${partner.partner_code}/change-password`, {
        current_password: currentPw,
        new_password: newPw
      });
      setCurrentPw('');
      setNewPw('');
      setPwMsg('Password changed successfully');
      setTimeout(() => setPwMsg(''), 3000);
    } catch (err) {
      setPwMsg(err.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await axios.post(`${API}/partner/login`, {
        partner_code: partnerCode,
        password
      });
      if (response.data.success) {
        const data = {
          partner_code: response.data.partner_code,
          name: response.data.name,
          contact: response.data.contact,
          token: response.data.token
        };
        localStorage.setItem('partner_session', JSON.stringify(data));
        setPartner(data);
        setIsAuth(true);
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partner_session');
    setIsAuth(false);
    setPartner(null);
    setPartnerCode('');
    setPassword('');
  };

  const openUpdateModal = (booking) => {
    // Issue 5 — alert the operator when modifying a still-BOOKED booking.
    if ((booking.tracking_status || 'BOOKED') === 'BOOKED') {
      const ok = window.confirm(
        `Heads up: Booking ${booking.baggage_tag || booking.booking_id} is still in BOOKED status (awaiting payment / scheduling).\n\nAny update you make now may surprise the customer. Continue?`
      );
      if (!ok) return;
    }
    setEditBooking(booking);
    setNewStatus(booking.tracking_status || 'BOOKED');
    setStatusLocation('');
    setStatusNotes('');
    setUpdateError('');
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    setUpdateError('');
    try {
      await axios.post(`${API}/partner/${partner.partner_code}/bookings/${editBooking.booking_id}/status`, {
        status: newStatus,
        location: statusLocation,
        notes: statusNotes
      });
      setEditBooking(null);
      fetchData();
    } catch (err) {
      setUpdateError(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Login Screen
  if (!isAuth) {
    return (
      <div className="theme-sunset flex items-center justify-center p-4">
        <div className="card-brutalist p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <BrandMark size={48} showText={false} className="justify-center mb-3" />
            <Truck size={36} color="#FF4500" strokeWidth={2} className="mx-auto mb-2" />
            <h1 className="text-2xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              PARTNER LOGIN
            </h1>
            <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
              DELIVERY PARTNER PORTAL
            </p>
          </div>
          <label className="block text-xs font-mono uppercase tracking-wide mb-2">PARTNER CODE</label>
          <input
            data-testid="partner-code-input"
            type="text"
            value={partnerCode}
            onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
            placeholder="Enter your partner code"
            className="w-full mb-4"
          />
          <label className="block text-xs font-mono uppercase tracking-wide mb-2">PASSWORD</label>
          <input
            data-testid="partner-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Enter password"
            className="w-full mb-4"
          />
          {loginError && (
            <p data-testid="partner-login-error" className="text-red-600 text-sm mb-4 font-mono">{loginError}</p>
          )}
          <button
            data-testid="partner-login-btn"
            onClick={handleLogin}
            disabled={loginLoading}
            className="btn-primary w-full py-3 disabled:opacity-50"
          >
            {loginLoading ? 'AUTHENTICATING...' : 'LOGIN'}
          </button>
          <InstallAppButton className="mt-3" label="INSTALL PARTNER APP" manifest="/manifest-partner.json" />
        </div>
      </div>
    );
  }

  return (
    <div className="theme-sunset p-4 md:p-8" data-testid="partner-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BrandMark size={36} />
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="px-2 py-1 bg-[#FF4500] text-black text-xs font-mono uppercase border-2 border-black">PARTNER</span>
            <div className="flex gap-2 items-center">
              <div className="text-right hidden md:block">
                <div className="text-xs font-mono uppercase text-gray-600">{partner?.partner_code}</div>
                <div className="text-sm font-bold font-mono">{partner?.name}</div>
              </div>
              <button data-testid="partner-logout-btn" onClick={handleLogout} className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm">
                <LogOut size={16} />
                LOGOUT
              </button>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
            {activeTab === 'bookings' && 'MY ASSIGNMENTS'}
            {activeTab === 'fleet' && 'LIVE FLEET MAP'}
            {activeTab === 'profile' && 'MY PROFILE'}
            {activeTab === 'audit' && 'ACTIVITY LOG'}
          </h1>
          <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
            {partner?.partner_code} · {bookings.length} TOTAL DELIVERIES
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b-2 border-black flex-wrap">
          <button
            data-testid="partner-tab-bookings"
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 flex items-center gap-2 ${activeTab === 'bookings' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'}`}
          >
            <Truck size={14} />
            BOOKINGS
          </button>
          <button
            data-testid="partner-tab-fleet"
            onClick={() => setActiveTab('fleet')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 flex items-center gap-2 ${activeTab === 'fleet' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'}`}
          >
            <Navigation size={14} />
            FLEET
          </button>
          <button
            data-testid="partner-tab-profile"
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 flex items-center gap-2 ${activeTab === 'profile' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'}`}
          >
            <User size={14} />
            PROFILE
          </button>
          <button
            data-testid="partner-tab-audit"
            onClick={() => setActiveTab('audit')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 flex items-center gap-2 ${activeTab === 'audit' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'}`}
          >
            <History size={14} />
            AUDIT LOG
          </button>
        </div>

        {activeTab === 'bookings' && (
        <>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="partner-stats">
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">TOTAL</div>
              <div className="text-3xl font-black font-mono">{stats.total_bookings}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">ACTIVE</div>
              <div className="text-3xl font-black font-mono" style={{ color: '#FF4500' }}>{stats.active_deliveries}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">DELIVERED</div>
              <div className="text-3xl font-black font-mono" style={{ color: '#00C853' }}>{stats.delivered}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">DELAYED</div>
              <div className="text-3xl font-black font-mono" style={{ color: '#DC2626' }}>{stats.delayed}</div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="flex justify-end mb-4">
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              data-testid="partner-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="SEARCH BY CUSTOMER NAME OR MOBILE"
              className="w-full pl-10 pr-9 py-2 font-mono text-xs uppercase tracking-wide"
            />
            {searchQuery && (
              <button
                data-testid="partner-search-clear-btn"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-600"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={16} />
            <span className="text-xs font-mono uppercase">FILTER:</span>
          </div>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt}
              data-testid={`partner-filter-${opt.toLowerCase()}`}
              onClick={() => setStatusFilter(opt)}
              className={`px-3 py-1 border-2 border-black text-xs font-mono uppercase ${
                statusFilter === opt ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              {opt === 'ALL' ? 'ALL' : STATUS_CONFIG[opt]?.label || opt}
            </button>
          ))}
        </div>

        {/* Bookings Grid */}
        {(() => {
          const q = searchQuery.trim().toLowerCase();
          const filtered = q
            ? bookings.filter(b =>
                (b.customer_name || '').toLowerCase().includes(q) ||
                (b.customer_phone || '').toLowerCase().includes(q)
              )
            : bookings;
          if (loading) return <div className="card-brutalist p-12 text-center loading-pulse font-mono">LOADING...</div>;
          if (bookings.length === 0) {
            return (
              <div className="card-brutalist p-12 text-center">
                <Package size={64} strokeWidth={1.5} color="#52525B" className="mx-auto mb-4" />
                <h2 className="text-xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>NO ASSIGNED BOOKINGS</h2>
                <p className="text-sm text-gray-600 font-mono">You don't have any deliveries assigned yet. Contact admin for assignments.</p>
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="card-brutalist p-12 text-center" data-testid="partner-no-search-match">
                <Search size={48} strokeWidth={1.5} color="#52525B" className="mx-auto mb-3" />
                <p className="text-sm text-gray-600 font-mono">No bookings match "<strong>{searchQuery}</strong>".</p>
              </div>
            );
          }
          return (
            <>
              {q && (
                <div className="mb-4 text-xs font-mono uppercase text-gray-600" data-testid="partner-search-count">
                  Showing {filtered.length} of {bookings.length}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="partner-bookings-list">
                {filtered.map((b) => {
                  const status = b.tracking_status || 'BOOKED';
                  const StatusIcon = STATUS_CONFIG[status]?.icon || Clock;
                  return (
                    <div
                      key={b.booking_id}
                      className="card-brutalist p-6"
                  data-testid={`partner-booking-${b.baggage_tag}`}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-dashed border-gray-300">
                    <div>
                      <div className="text-xs font-mono uppercase text-gray-600">BAGGAGE TAG</div>
                      <div className="text-2xl font-black font-mono">{b.baggage_tag}</div>
                    </div>
                    <div
                      className="flex items-center gap-2 px-3 py-1 border-2 border-black"
                      style={{ backgroundColor: STATUS_CONFIG[status]?.color }}
                    >
                      <StatusIcon size={14} color="#000" />
                      <span className="font-bold uppercase text-xs">
                        {STATUS_CONFIG[status]?.label}
                      </span>
                    </div>
                  </div>

                  {/* Customer */}
                  <div className="mb-4 p-3 bg-gray-50 border-2 border-black">
                    <div className="text-xs font-mono uppercase text-gray-600 mb-1">CUSTOMER</div>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <div className="font-bold">{b.customer_name}</div>
                        {b.customer_phone && (
                          <div className="text-xs font-mono text-gray-600 mt-1">{b.customer_phone}</div>
                        )}
                      </div>
                      {b.customer_phone && (
                        <CallCustomerButton phone={b.customer_phone} size="xs" />
                      )}
                    </div>
                  </div>

                  {/* Route */}
                  <div className="mb-4">
                    <div className="data-label mb-1 flex items-center gap-1">
                      <MapPin size={12} />
                      ROUTE
                    </div>
                    <div className="text-sm font-mono">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="font-bold">{b.pickup_location || 'N/A'}</div>
                        <ViewMapButton booking={b} type="pickup" size="xs" />
                      </div>
                      <div className="text-xs text-gray-600 break-words">{formatPickupAddress(b)}</div>
                      <div className="text-orange-600 font-bold mt-1">↓</div>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="font-bold">{b.delivery_location}</div>
                        <ViewMapButton booking={b} type="delivery" size="xs" />
                      </div>
                      {formatDeliveryAddress(b) && (
                        <div className="text-xs text-gray-600 break-words">{formatDeliveryAddress(b)}</div>
                      )}
                    </div>
                  </div>

                  {/* Luggage Summary */}
                  <div className="grid grid-cols-2 gap-2 mb-4 pb-4 border-b-2 border-dashed border-gray-300">
                    <div>
                      <div className="data-label text-[10px]">BAGS</div>
                      <div className="data-value text-sm">{b.luggage_details.quantity || (b.luggage_details.bags?.length || 0)}</div>
                    </div>
                    <div>
                      <div className="data-label text-[10px]">WEIGHT</div>
                      <div className="data-value text-sm">{b.luggage_details.weight} KG</div>
                    </div>
                  </div>

                  {/* Agent Assignment */}
                  <div className="mb-3 p-3 bg-[#FFF7E6] border-2 border-black">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-mono uppercase font-bold flex items-center gap-1">
                        <UserCog size={12} color="#FFB800" />
                        FIELD AGENT
                      </div>
                      {b.agent_code && (
                        <button
                          data-testid={`partner-clear-agent-${b.baggage_tag}`}
                          onClick={() => handleAssignAgent(b.booking_id, '')}
                          className="text-[10px] font-mono underline text-gray-500 hover:text-red-600"
                        >
                          clear
                        </button>
                      )}
                    </div>
                    {b.agent_code ? (
                      <div data-testid={`partner-booking-agent-${b.baggage_tag}`}>
                        <div className="text-sm font-bold font-mono">{b.agent_code}</div>
                        {b.agent_name && <div className="text-xs">{b.agent_name}</div>}
                        {b.agent_contact && (
                          <a href={`tel:${b.agent_contact}`} className="text-xs font-mono text-orange-600 hover:underline flex items-center gap-1 mt-1">
                            <Phone size={10} />{b.agent_contact}
                          </a>
                        )}
                      </div>
                    ) : (
                      <select
                        data-testid={`partner-assign-agent-${b.baggage_tag}`}
                        value=""
                        onChange={(e) => e.target.value && handleAssignAgent(b.booking_id, e.target.value)}
                        className="w-full text-xs py-1 px-2"
                      >
                        <option value="">— SELECT AGENT —</option>
                        {availableAgents.map(a => (
                          <option key={a.agent_code} value={a.agent_code}>{a.agent_code} — {a.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                      {/* Action */}
                      <div className="flex gap-2 flex-wrap">
                        {OTP_ELIGIBLE_STATES.has(status) && (
                          <button
                            data-testid={`partner-deliver-otp-${b.baggage_tag}`}
                            onClick={() => setOtpBooking(b)}
                            className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 bg-[#00C853]"
                            title="Verify customer via OTP and mark delivered"
                          >
                            <Shield size={14} />
                            DELIVER & VERIFY (OTP)
                          </button>
                        )}
                        <button
                          data-testid={`partner-quick-update-${b.baggage_tag}`}
                          onClick={() => openUpdateModal(b)}
                          disabled={(STATUS_TRANSITIONS_PARTNER[b.tracking_status || 'BOOKED'] || []).length === 0}
                          className="btn-primary flex-1 min-w-[140px] py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Update status directly"
                        >
                          <Truck size={14} />
                          UPDATE STATUS
                        </button>
                        <button
                          data-testid={`partner-view-details-${b.baggage_tag}`}
                          onClick={() => setViewingBooking(b)}
                          className="btn-secondary flex-1 min-w-[140px] py-2.5 text-sm flex items-center justify-center gap-2"
                        >
                          <Eye size={14} />
                          VIEW & VERIFY
                        </button>
                        <button
                          data-testid={`partner-print-tag-${b.baggage_tag}`}
                          onClick={() => handlePrint(b)}
                          className="btn-secondary py-2.5 px-3 text-sm flex items-center gap-1"
                          title="Print baggage tag"
                        >
                          <Printer size={14} />
                          PRINT
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
        </>
        )}

        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div className="space-y-6" data-testid="partner-profile-tab">
            <div className="card-brutalist p-6">
              <h2 className="text-lg font-bold uppercase mb-4 pb-2 border-b-2 border-black" style={{ fontFamily: 'IBM Plex Mono' }}>
                PARTNER INFO
              </h2>
              <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600">PARTNER CODE (LOCKED)</div>
                <div className="text-2xl font-black font-mono">{partner?.partner_code}</div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">NAME</label>
                  <input
                    data-testid="profile-name-input"
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">CONTACT</label>
                  <input
                    data-testid="profile-contact-input"
                    type="text"
                    value={profileContact}
                    onChange={(e) => setProfileContact(e.target.value)}
                    placeholder="Phone or email"
                    className="w-full"
                  />
                </div>
                {profileMsg && (
                  <p data-testid="profile-msg" className={`text-sm font-mono p-2 border-2 ${profileMsg.includes('success') ? 'border-green-600 text-green-600 bg-green-50' : 'border-red-600 text-red-600 bg-red-50'}`}>
                    {profileMsg}
                  </p>
                )}
                <button data-testid="save-profile-btn" onClick={handleUpdateProfile} className="btn-primary px-6 py-2">
                  SAVE PROFILE
                </button>
              </div>
            </div>

            <div className="card-brutalist p-6">
              <h2 className="text-lg font-bold uppercase mb-4 pb-2 border-b-2 border-black flex items-center gap-2" style={{ fontFamily: 'IBM Plex Mono' }}>
                <Lock size={18} />
                CHANGE PASSWORD
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">CURRENT PASSWORD</label>
                  <input
                    data-testid="current-pw-input"
                    type="password"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">NEW PASSWORD</label>
                  <input
                    data-testid="new-pw-input"
                    type="password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="Min 4 characters"
                    className="w-full"
                  />
                </div>
                {pwMsg && (
                  <p data-testid="pw-msg" className={`text-sm font-mono p-2 border-2 ${pwMsg.includes('success') ? 'border-green-600 text-green-600 bg-green-50' : 'border-red-600 text-red-600 bg-red-50'}`}>
                    {pwMsg}
                  </p>
                )}
                <button
                  data-testid="change-pw-btn"
                  onClick={handleChangePassword}
                  disabled={!currentPw || newPw.length < 4}
                  className="btn-primary px-6 py-2 disabled:opacity-50"
                >
                  CHANGE PASSWORD
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FLEET TAB */}
        {activeTab === 'fleet' && (
          <div data-testid="partner-fleet-tab">
            <PartnerFleetMap partnerCode={partner?.partner_code} />
          </div>
        )}

        {/* AUDIT LOG TAB */}
        {activeTab === 'audit' && (
          <div className="card-brutalist p-0 overflow-hidden" data-testid="partner-audit-tab">
            <div className="px-6 py-4 border-b-2 border-black bg-white flex items-center justify-between">
              <h2 className="text-lg font-bold uppercase" style={{ fontFamily: 'IBM Plex Mono' }}>
                STATUS UPDATES BY YOU
              </h2>
              <span className="text-xs font-mono uppercase text-gray-600">{auditLog.length} ENTRIES</span>
            </div>
            {auditLog.length === 0 ? (
              <div className="p-12 text-center">
                <History size={48} strokeWidth={1.5} color="#52525B" className="mx-auto mb-3" />
                <p className="font-mono text-gray-600 text-sm">No status updates yet. Updates you make will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b-2 border-black">
                    <tr>
                      <th className="text-left p-3 font-mono uppercase text-xs">Tag</th>
                      <th className="text-left p-3 font-mono uppercase text-xs">Customer</th>
                      <th className="text-left p-3 font-mono uppercase text-xs">Status</th>
                      <th className="text-left p-3 font-mono uppercase text-xs">Location</th>
                      <th className="text-left p-3 font-mono uppercase text-xs">Notes</th>
                      <th className="text-left p-3 font-mono uppercase text-xs">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map((entry, idx) => {
                      const StatusIcon = STATUS_CONFIG[entry.status]?.icon || Clock;
                      return (
                        <tr key={`${entry.baggage_tag}-${entry.status}-${entry.timestamp || idx}`} className="border-b border-gray-200" data-testid={`audit-row-${idx}`}>
                          <td className="p-3 font-mono font-bold">{entry.baggage_tag}</td>
                          <td className="p-3 text-xs">{entry.customer_name}</td>
                          <td className="p-3">
                            <div
                              className="inline-flex items-center gap-1 px-2 py-1 border-2 border-black text-xs font-bold uppercase"
                              style={{ backgroundColor: STATUS_CONFIG[entry.status]?.color }}
                            >
                              <StatusIcon size={10} color="#000" />
                              {STATUS_CONFIG[entry.status]?.label}
                            </div>
                          </td>
                          <td className="p-3 text-xs font-mono">{entry.location || '—'}</td>
                          <td className="p-3 text-xs">{entry.notes || '—'}</td>
                          <td className="p-3 text-xs font-mono">
                            {new Date(entry.timestamp).toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', year: '2-digit',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      {editBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="partner-update-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                UPDATE STATUS
              </h2>
              <button onClick={() => setEditBooking(null)} className="hover:text-orange-600">
                <X size={24} />
              </button>
            </div>
            <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
              <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGGAGE TAG</div>
              <div className="text-xl font-black font-mono">{editBooking.baggage_tag}</div>
              <div className="text-xs font-mono text-gray-600 mt-2">{editBooking.customer_name} · {editBooking.customer_phone}</div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">
                  NEW STATUS
                  <span className="ml-2 text-gray-500 font-normal normal-case">(from {STATUS_CONFIG[editBooking.tracking_status || 'BOOKED']?.label || editBooking.tracking_status})</span>
                </label>
                {(() => {
                  const allowed = STATUS_TRANSITIONS_PARTNER[editBooking.tracking_status || 'BOOKED'] || [];
                  if (allowed.length === 0) {
                    return (
                      <div className="border-2 border-black p-4 bg-gray-100 text-xs font-mono uppercase text-gray-600" data-testid="partner-status-no-transitions">
                        TERMINAL STATE — NO FURTHER STATUS CHANGES ALLOWED FROM "{STATUS_CONFIG[editBooking.tracking_status]?.label || editBooking.tracking_status}".
                      </div>
                    );
                  }
                  return (
                    <div data-testid="partner-status-radio-group" className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto border-2 border-black p-2 bg-white">
                      {allowed.map(s => {
                        const cfg = STATUS_CONFIG[s] || {};
                        const checked = newStatus === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            data-testid={`partner-status-radio-${s.toLowerCase()}`}
                            onClick={() => setNewStatus(s)}
                            className={`flex items-center gap-2 px-3 py-2 border-2 border-black text-xs font-mono uppercase font-bold text-left transition-colors ${checked ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'}`}
                          >
                            <span className={`inline-block w-3 h-3 rounded-full border-2 border-black ${checked ? 'bg-black' : 'bg-white'}`}></span>
                            <span className="flex-1">{cfg.label || s}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">NOTES</label>
                <textarea
                  data-testid="partner-status-notes"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Additional details"
                  rows={3}
                  className="w-full"
                />
              </div>
              {updateError && (
                <p data-testid="partner-update-error" className="text-red-600 text-sm font-mono">{updateError}</p>
              )}
              <button
                data-testid="partner-confirm-update"
                onClick={handleUpdateStatus}
                disabled={updating || newStatus === editBooking.tracking_status || (STATUS_TRANSITIONS_PARTNER[editBooking.tracking_status || 'BOOKED'] || []).length === 0}
                className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'UPDATING...' : (newStatus === editBooking.tracking_status ? 'SELECT A NEW STATUS' : 'UPDATE & NOTIFY CUSTOMER')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View & Verify Luggage Details Modal */}
      {viewingBooking && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-start sm:items-center justify-center p-4 z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setViewingBooking(null); }}
          data-testid="partner-view-details-overlay"
        >
          <div className="card-brutalist bg-white max-w-3xl w-full max-h-[90vh] flex flex-col" data-testid="partner-view-details-modal">
            <div className="flex items-center justify-between p-6 pb-4 border-b-2 border-black bg-white sticky top-0 rounded-t-[inherit]">
              <div>
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  VERIFY LUGGAGE
                </h2>
                <p className="text-xs font-mono uppercase text-gray-600">
                  CROSS-CHECK ALL ITEMS BEFORE STATUS UPDATE
                </p>
              </div>
              <button data-testid="partner-close-details-modal" onClick={() => setViewingBooking(null)} aria-label="Close details" className="p-2 -m-2 hover:text-orange-600">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 pt-4 flex-1">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-gray-100 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGGAGE TAG</div>
                <div className="text-xl font-black font-mono">{viewingBooking.baggage_tag}</div>
              </div>
              <div className="p-3 bg-gray-50 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1">CUSTOMER</div>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-bold">{viewingBooking.customer_name}</div>
                    {viewingBooking.customer_phone && (
                      <div className="text-xs font-mono text-gray-600 mt-1">{viewingBooking.customer_phone}</div>
                    )}
                  </div>
                  {viewingBooking.customer_phone && (
                    <CallCustomerButton phone={viewingBooking.customer_phone} size="sm" />
                  )}
                </div>
              </div>
            </div>

            <div className="p-3 bg-gray-50 border-2 border-black mb-4">
              <div className="text-xs font-mono uppercase text-gray-600 mb-1 flex items-center gap-1">
                <MapPin size={10} />ROUTE
              </div>
              <div className="text-sm font-mono space-y-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-bold">PICKUP — {viewingBooking.pickup_location}</span>
                    <span className="text-gray-600"> ({formatPickupAddress(viewingBooking)})</span>
                  </div>
                  <ViewMapButton booking={viewingBooking} type="pickup" size="sm" />
                </div>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-bold">DROP — {viewingBooking.delivery_location}</span>
                    {formatDeliveryAddress(viewingBooking) && (
                      <span className="text-gray-600"> ({formatDeliveryAddress(viewingBooking)})</span>
                    )}
                  </div>
                  <ViewMapButton booking={viewingBooking} type="delivery" size="sm" />
                </div>
              </div>
            </div>

            {(viewingBooking.scheduled_pickup_date || viewingBooking.scheduled_pickup_time) && (
              <div className="p-3 bg-[#FFF7E6] border-2 border-black mb-4" data-testid="partner-scheduled-pickup">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1 flex items-center gap-1">
                  <Calendar size={10}/>SCHEDULED PICKUP
                </div>
                <div className="text-sm font-bold font-mono">
                  {viewingBooking.scheduled_pickup_date || '—'}{viewingBooking.scheduled_pickup_time ? ` · ${formatPickupSlot(viewingBooking.scheduled_pickup_time)}` : ''}
                </div>
              </div>
            )}

            <div className="mb-4">
              <h3 className="text-sm font-black uppercase mb-3 font-mono">
                BAGS ({viewingBooking.luggage_details.bags?.length || 0})
              </h3>
              <div className="space-y-4">
                {(viewingBooking.luggage_details.bags || []).map((bag, idx) => (
                  <div key={idx} className="border-2 border-black p-4 bg-white" data-testid={`partner-bag-detail-${idx}`}>
                    <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-dashed border-gray-300">
                      <div className="font-mono uppercase font-black text-base">BAG #{idx + 1}</div>
                      <div className="px-2 py-1 bg-black text-white text-xs font-mono uppercase">
                        {(bag.bag_type || 'BAG').toUpperCase()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3 text-xs">
                      <div>
                        <div className="data-label flex items-center gap-1"><Tag size={10}/>BRAND/COMPANY</div>
                        <div className="font-bold">{bag.brand || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label">COLOR</div>
                        <div className="font-bold">{bag.color || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label">MATERIAL</div>
                        <div className="font-bold uppercase">{(bag.material || 'soft_fabric').replace('_', ' ')}</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Weight size={10}/>WEIGHT</div>
                        <div className="font-bold">{bag.weight} KG</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Ruler size={10}/>DIMENSIONS</div>
                        <div className="font-bold">{bag.dimensions || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Shield size={10}/>HANDLING</div>
                        <div className="font-bold uppercase">{bag.handling_type || 'standard'}</div>
                      </div>
                      <div>
                        <div className="data-label flex items-center gap-1"><Lock size={10}/>LOCK</div>
                        <div className="font-bold uppercase">{bag.lock_type || 'none'}</div>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <div className="data-label">DISTINGUISHING MARKS</div>
                        <div className="font-bold">{bag.distinguishing_marks || '—'}</div>
                      </div>
                    </div>

                    <div>
                      <div className="data-label mb-2 flex items-center gap-1">
                        <ImageIcon size={10}/>
                        CUSTOMER PHOTOS ({(bag.images || []).length})
                      </div>
                      {(bag.images || []).length === 0 ? (
                        <div className="text-xs font-mono text-gray-500 italic p-3 border-2 border-dashed border-gray-300 text-center">
                          No photos uploaded by customer
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {(bag.images || []).map((img, imgIdx) => (
                            <button
                              key={imgIdx}
                              type="button"
                              onClick={() => openPhoto(bag.images || [], imgIdx)}
                              data-testid={`partner-bag-${idx}-photo-${imgIdx}`}
                              className="block border-2 border-black overflow-hidden hover:shadow-lg transition-shadow cursor-zoom-in"
                              title="Click to enlarge"
                            >
                              <img
                                src={img}
                                alt={`Bag ${idx + 1} photo ${imgIdx + 1}`}
                                className="w-full h-24 object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            </div>
            <div className="p-6 pt-4 border-t-2 border-black bg-white flex flex-col sm:flex-row gap-3 sticky bottom-0 rounded-b-[inherit]">
              <button
                data-testid="partner-cancel-verify"
                onClick={() => setViewingBooking(null)}
                className="btn-secondary px-4 py-3 text-xs flex-1"
              >
                CLOSE
              </button>
              {(viewingBooking?.tracking_status || '').toUpperCase() === 'SCHEDULED' && (
                <button
                  data-testid="partner-request-edit"
                  onClick={() => {
                    setEditingBooking(viewingBooking);
                    setViewingBooking(null);
                  }}
                  className="btn-secondary px-4 py-3 text-xs flex-1 flex items-center justify-center gap-2 border-orange-500 text-orange-600"
                  title="Edit manifest with customer OTP authorisation (SCHEDULED bookings only)"
                >
                  <ShieldCheck size={14} /> REQUEST EDIT (OTP)
                </button>
              )}
              <button
                data-testid="partner-confirm-and-update"
                onClick={() => {
                  const booking = viewingBooking;
                  setViewingBooking(null);
                  openUpdateModal(booking);
                }}
                className="btn-primary px-4 py-3 text-xs flex-1 flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={14} />
                CONFIRM & UPDATE STATUS
              </button>
            </div>
          </div>
        </div>
      )}

      {editingBooking && partner && (
        <EditAuthorisationModal
          role="partner"
          roleCode={partner.partner_code}
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSaved={(b) => { if (b) setEditingBooking((prev) => ({ ...prev, ...b })); fetchData(); }}
        />
      )}

      {/* Printable baggage tag */}
      {printingBooking && (
        <BaggageTagPrint booking={printingBooking.booking} customer={printingBooking.customer} />
      )}

      {/* Delivery OTP Modal */}
      {otpBooking && partner && (
        <DeliveryOtpModal
          role="partner"
          roleCode={partner.partner_code}
          booking={otpBooking}
          onClose={() => setOtpBooking(null)}
          onDelivered={fetchData}
        />
      )}
      {photoGallery}
    </div>
  );
};

export default Partner;
